import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { Button, Card, Stack } from '@mui/material';

//style
import {
  StyledCard,
  StyledCardContent,
  StyledStatusBox,
  StyledTypographySpan,
  StyledTypographySubTitle
} from '../../styled-components';
//constants
import {
  Site,
  SiteFormFields,
  SiteFormFieldTitles,
  SiteInformationForm,
  SiteInputsForm
} from 'constants/sites.constants';
import { pagesEnum } from 'constants/ui.constants';
//component
import CustomInputForm from '../../../components/InputsForm/CustomInput';
import ConfirmDialog from 'components/ConfirmDialog';
import { convertKeysToSnakeCase, formResolver, getFormattedDate } from '../../../utils/global.util';
//icons
import { CityIcon } from 'assets/icons/City.icon';
import { CpIcon } from 'assets/icons/Cp.icon';
import { SwiftIcon } from 'assets/icons/Swift.icon';
import { LocationIcon } from 'assets/icons/Location.icon';
import { PermissionIcon } from 'assets/icons/Permission.icon';
import { CalenderIcon } from 'assets/icons/Calender.icon';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { SiteMember } from '../SiteMember';
import { EngineerIcon } from 'assets/icons/Engineer.icon';
import { DoctorIcon } from 'assets/icons/Doctor.icon';
import { PatientIcon } from 'assets/icons/Patient.icon';
import { EditIcon } from 'assets/icons/Edit.icon';
import { inactiveSite, updateSite } from 'state/reducers/site.reducers';
import { UserRoleForFilter } from 'constants/user.constants';
import _ from 'lodash';
import { TabsSwitcher } from 'components/TabsSwitcher';
import ArrowBackBtn from 'components/ArrowBackBtn/arrowBackBtn';
import { DataFetchKeys, useDataFetchContext } from '../../../context/DataFetchContext';
import { setError } from 'state/reducers/site.reducers';
import { AppErrorCodes, ErrorMessages } from 'constants/errorMessages.constants';

const URL_Filter: Record<string, string> = {
  BWMR: '?userType=BWMR',
  HCP: '?userType=HCP'
} as const;

interface IFormInputs {
  dataForm: SiteInputsForm;
  openProfileModal: boolean;
  setOpenProfileModal: Dispatch<SetStateAction<boolean>>;
}

const SiteInformation: React.FC = () => {
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { loadDataByKeys } = useDataFetchContext();

  // Get site data from location state safely
  const site = location.state ? (location.state as { site: Site }).site : undefined;

  // Redux state
  const { sites, loading, error } = useAppSelector((state) => state.sites);
  const { cps } = useAppSelector((state) => state.cp);
  // Local state
  const [siteData, setSiteData] = useState<Site | undefined>(site);
  const [originalData, setOriginalData] = useState<Site | undefined>(undefined);
  const [formError, setFormError] = useState<{ [key: string]: boolean }>({});
  const [onEdit, setOnEdit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const methods = useForm<IFormInputs>({
    defaultValues: {
      dataForm: {}
    },
    mode: 'onTouched',
    resolver: (values) =>
      formResolver(values, [
        { key: SiteFormFields.NAME, displayName: SiteFormFieldTitles.NAME },
        {
          key: SiteFormFields.SITE_ID,
          displayName: SiteFormFieldTitles.SITE_ID
        },
        {
          key: SiteFormFields.COUNTRY,
          displayName: SiteFormFieldTitles.COUNTRY
        },
        { key: SiteFormFields.CITY, displayName: SiteFormFieldTitles.CITY }
      ])
  });

  const parsedSite = {
    ...site,
    createdAt: getFormattedDate(new Date(site?.createdAt || '')),
    updatedAt: getFormattedDate(new Date(site?.updatedAt || ''))
  };

  const {
    watch,
    reset,
    formState: { errors }
  } = methods;

  // Watch form fields
  const name = watch('dataForm.name');
  const siteId = watch('dataForm.siteId');
  const country = watch('dataForm.country');
  const city = watch('dataForm.city');

  // Computed values
  const isDisabled =
    !name?.trim() ||
    !siteId?.trim() ||
    !country?.trim() ||
    !city?.trim() ||
    _.isEqual(originalData, {
      name,
      siteId,
      country,
      city
    }) ||
    Object.keys(formError).length > 0;

  //in case the site has patients, users or CPs, we will not allow to inactive the site
  const siteHasActive =
    !!siteData?.activeCpCount ||
    !!siteData?.activeAdminCount ||
    !!siteData?.activeDoctorCount ||
    !!siteData?.activePatientCount;
  const errorMessage = error?.errorMessage;

  // Helper functions
  const getFullName = (firstName = '', lastName = '') => {
    if (!firstName && !lastName) return 'N/A';
    return `${firstName || ''} ${lastName || ''}`;
  };

  // Load initial data
  useEffect(() => {
    if (!site) {
      loadDataByKeys([DataFetchKeys.SITES]);
    }
    if (!cps?.length) {
      loadDataByKeys([DataFetchKeys.CPS]);
    }
  }, [site, cps?.length, loadDataByKeys]);

  // Initialize form data
  useEffect(() => {
    if (siteData && cps && id) {
      const assignedCps = cps?.filter((cp) => siteData?.name === cp?.assignedSite)?.length || 0;

      setOriginalData({
        name: siteData.name || '',
        country: siteData.country || '',
        siteId: siteData.siteId || '',
        city: siteData.city || ''
      });

      reset({
        dataForm: {
          name: siteData.name || '',
          country: siteData.country || '',
          siteId: siteData.siteId || '',
          city: siteData.city || '',
          createdAt: `${getFullName(siteData.createdByFirstName, siteData.createdByLastName)} ${
            getFormattedDate(new Date(siteData.createdAt || '')) as string
          }`,
          lastActivity: siteData.lastActivity || '',
          updatedAt: `${getFullName(siteData.updatedByFirstName, siteData.updatedByLastName)} ${
            getFormattedDate(new Date(siteData.updatedAt || '')) as string
          }`,
          activeCpCount: assignedCps
        }
      });
    }
  }, [siteData, id, cps, reset]);

  useEffect(() => {
    const loadSiteData = async () => {
      if (id) {
        const siteFromSites = sites?.find((site) => site.id === +id);

        if (onEdit) setOnEdit(false);
        if (siteFromSites) {
          setSiteData(siteFromSites);
          return;
        }

        try {
          await loadDataByKeys([DataFetchKeys.SITES]);
          const loadedSite = sites?.find((site) => site.id === +id);
          if (loadedSite) {
            setSiteData(loadedSite);
          }
        } catch (err) {
          setFormError((prev) => ({ ...prev, loading: true }));
        }
      } else if (site) {
        setSiteData(site);
      }
    };

    loadSiteData();
  }, [id, sites, loadDataByKeys, site]);

  const validateField = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    // skip validation if the field is empty or if isn't name or siteId
    if (!value?.trim() || !['name', 'siteId'].includes(id) || !sites) {
      setFormError((prev) => _.omit(prev, id));
      return;
    }

    const exist = sites.some(
      (_site: Site) =>
        _site[id as keyof SiteInformationForm]?.toLowerCase() === value?.toLowerCase() && _site.id !== siteData?.id
    );
    // set or remove error depends on exists
    setFormError((prev) => (exist ? { ...prev, [id]: exist } : _.omit(prev, id)));
  };

  useEffect(() => {
    return () => {
      // this response for error cleanup the from API call
      dispatch(setError(null));
    };
  }, [dispatch]);

  // Form submission handler
  const formSubmitHandler: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    const site = convertKeysToSnakeCase({
      name,
      country,
      siteId,
      city
    });

    await dispatch(updateSite({ id, site }));
    setOnEdit(false);
  };

  const successHandler = () => {
    setOpenDialog(false);
    setOnEdit(false);
  };

  // Dialog handlers
  const onConfirmHandler = async () => {
    //if we already receive an error message that indicates that we already send api request, we will close the dialog on ok and remove the error
    if (errorMessage) {
      dispatch(setError(null));
      setOpenDialog(false);
      return;
    }
    // if we have active users/patients/cps, we will not allow inactivating the site
    if (siteHasActive && siteData?.active) {
      setOpenDialog(false);
      return;
    }
    let response;
    if (siteData?.active) {
      response = await dispatch(
        inactiveSite({
          siteId: id
        })
      );
    } else {
      response = await dispatch(updateSite({ id, site: { active: !siteData?.active } }));
    }

    // in case the request is fulfilled, we will call the success handler
    if (response?.meta?.requestStatus === 'fulfilled') {
      successHandler();
    }
  };

  // Handle form cancellation
  const handleCancel = () => {
    reset();
    setFormError({});
    setOnEdit(false);
  };

  const getDialogText = (): string => {
    //in case we want to inactivate site=> first check if the site has active users or permissions or cps or patients and show the same error as the API on the same case
    if (siteHasActive && siteData?.active) {
      // activeUsers is the sum of fcs and hcp
      const activeUsers = (siteData?.activeAdminCount || 0) + (siteData?.activeDoctorCount || 0);
      const activePatients = siteData?.activePatientCount || 0;
      const activeCps = siteData?.activeCpCount || 0;
      return ErrorMessages[AppErrorCodes.SITE_INACTIVATION_ERROR]({
        activeUsers,
        activePatients,
        activeCps
      });
    }
    if (errorMessage) {
      return errorMessage;
    }
    return 'Press OK to continue';
  };

  if (!siteData && !loading) return null;
  if (loading) return null;
  return (
    <FormProvider {...methods}>
      <form noValidate onChange={validateField} onSubmit={methods.handleSubmit(formSubmitHandler)}>
        <StyledCard sx={{ background: 'transparent' }}>
          <StyledCardContent>
            <Stack spacing={3}>
              <ArrowBackBtn title={'Site Info'} route={`/${pagesEnum.SITES}`} />
              <Stack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
                <Stack spacing={2} direction='row' alignItems='center'>
                  <StyledTypographySubTitle>
                    {onEdit ? 'Edit Site' : siteData?.name || site?.name}
                  </StyledTypographySubTitle>
                  {onEdit ? (
                    <TabsSwitcher
                      onClick={() => {
                        onEdit ? setOpenDialog(true) : {};
                      }}
                      isInactive={!siteData?.active}
                      disabled={!onEdit}
                    />
                  ) : (
                    <StyledStatusBox isInactive={!siteData?.active}>
                      {!siteData?.active ? 'Inactive' : 'Active'}
                    </StyledStatusBox>
                  )}
                </Stack>
                {!onEdit && (
                  <Button
                    color='secondary'
                    data-testid='edit-button'
                    startIcon={<EditIcon />}
                    variant='outlined'
                    onClick={() => {
                      setOnEdit(true);
                    }}
                  >
                    Edit Site
                  </Button>
                )}
                <ConfirmDialog
                  openDialog={openDialog}
                  setOpenDialog={setOpenDialog}
                  onConfirm={onConfirmHandler}
                  errorDialog={siteData?.active && (siteHasActive || errorMessage)}
                  dialogTitle={
                    siteData?.active && (siteHasActive || errorMessage)
                      ? `You cannot convert ${site?.name} site to inactive`
                      : siteData?.active
                      ? 'Convert site to inactive'
                      : 'Convert site to active'
                  }
                  showCancel={!errorMessage && !siteHasActive}
                  dialogContentText={getDialogText()}
                />
              </Stack>
              <Card variant='outlined' sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {onEdit && (
                    <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                      <CustomInputForm
                        disabled={!onEdit}
                        defaultValue={site?.name}
                        controllerName={'dataForm.name'}
                        inputRegister={'dataForm.name'}
                        inputId={'name'}
                        label={SiteFormFieldTitles.NAME}
                        type={'text'}
                        icon={<LocationIcon />}
                        errorMessage={
                          errors.dataForm?.name?.message ||
                          (_.get(formError, 'name') ? 'Same site name, Try other name.' : '')
                        }
                      />

                      <CustomInputForm
                        disabled={!onEdit}
                        defaultValue={site?.country}
                        controllerName={'dataForm.country'}
                        inputRegister={'dataForm.country'}
                        inputId={'country'}
                        label={SiteFormFieldTitles.COUNTRY}
                        type={'text'}
                        icon={<SwiftIcon />}
                        errorMessage={errors.dataForm?.country?.message}
                      />
                    </Stack>
                  )}
                  <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                    {!onEdit && (
                      <CustomInputForm
                        disabled={!onEdit}
                        defaultValue={siteData?.country}
                        controllerName={'dataForm.country'}
                        inputRegister={'dataForm.country'}
                        inputId={'country'}
                        label={SiteFormFieldTitles.COUNTRY}
                        type={'text'}
                        icon={<SwiftIcon />}
                        errorMessage={errors.dataForm?.country?.message}
                      />
                    )}
                    <CustomInputForm
                      disabled={!onEdit}
                      defaultValue={siteData?.siteId}
                      controllerName={'dataForm.siteId'}
                      inputRegister={'dataForm.siteId'}
                      inputId={'siteId'}
                      label={SiteFormFieldTitles.SITE_ID}
                      type={'text'}
                      icon={<PermissionIcon />}
                      errorMessage={
                        errors.dataForm?.siteId?.message ||
                        (_.get(formError, 'siteId') ? 'Same site ID, Try other ID.' : '')
                      }
                    />
                    <CustomInputForm
                      disabled={!onEdit}
                      defaultValue={siteData?.city}
                      controllerName={'dataForm.city'}
                      inputRegister={'dataForm.city'}
                      inputId={'city'}
                      label={SiteFormFieldTitles.CITY}
                      type={'text'}
                      icon={<CityIcon />}
                      errorMessage={errors.dataForm?.city?.message}
                    />
                    <CustomInputForm
                      disabled={true}
                      defaultValue={`${parsedSite?.createdByFirstName} ${parsedSite?.createdByLastName} ${parsedSite?.createdAt}`}
                      controllerName={'dataForm.createdAt'}
                      inputRegister={'dataForm.createdAt'}
                      inputId={'createdAt'}
                      label={'Created By'}
                      type={'text'}
                      icon={<CalenderIcon />}
                    />
                  </Stack>
                </Stack>
              </Card>
              <Stack spacing={2}>
                <StyledTypographySubTitle>Assigned Member</StyledTypographySubTitle>
                <StyledTypographySpan>Click to see more information.</StyledTypographySpan>
                <Stack spacing={5} direction='row' sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <SiteMember
                    title={'CP'}
                    icon={<CpIcon />}
                    activeCount={siteData?.activeCpCount || 0}
                    inactiveCount={(siteData?.cpCount || 0) - (siteData?.activeCpCount || 0)}
                    params={{ sites: siteData?.name || '' }}
                    to={methods.getValues('dataForm.activeCpCount') ? pagesEnum.CP : ''}
                  />
                  <SiteMember
                    title={'BWMR'}
                    icon={<EngineerIcon />}
                    activeCount={siteData?.activeAdminCount || 0}
                    inactiveCount={(siteData?.adminCount || 0) - (siteData?.activeAdminCount || 0)}
                    params={{ userType: UserRoleForFilter.ADMIN, sites: siteData?.name || '' }}
                    to={siteData?.adminCount ? pagesEnum.USERS_AND_PERMISSIONS : ''}
                  />
                  <SiteMember
                    title={'HCP'}
                    icon={<DoctorIcon />}
                    activeCount={siteData?.activeDoctorCount || 0}
                    inactiveCount={(siteData?.doctorCount || 0) - (siteData?.activeDoctorCount || 0)}
                    params={{ userType: UserRoleForFilter.DOCTOR, sites: siteData?.name || '' }}
                    to={siteData?.doctorCount ? pagesEnum.USERS_AND_PERMISSIONS : ''}
                  />
                  <SiteMember
                    title={'Patient'}
                    icon={<PatientIcon />}
                    activeCount={siteData?.activePatientCount || 0}
                    inactiveCount={(siteData?.patientCount || 0) - (siteData?.activePatientCount || 0)}
                    params={{ sites: siteData?.name || '' }}
                    to={siteData?.patientCount ? pagesEnum.PATIENTS : ''}
                  />
                </Stack>
              </Stack>
            </Stack>
          </StyledCardContent>
        </StyledCard>
        {onEdit && (
          <Stack spacing={2} direction='row' sx={{ display: 'flex', justifyContent: 'flex-end', m: 10 }}>
            <Button color='secondary' data-testid='cancel-button' size='large' onClick={handleCancel}>
              cancel
            </Button>
            <Button
              type={'submit'}
              disabled={isDisabled}
              color='secondary'
              variant='contained'
              data-testid='save-button'
            >
              save
            </Button>
          </Stack>
        )}
      </form>
    </FormProvider>
  );
};

export default SiteInformation;
